import React, { useState, useEffect, useRef } from 'react';
import {
  Cpu,
  Activity,
  Shield,
  Zap,
  Globe,
  Code,
  ChevronRight,
  Menu,
  X,
  TrendingUp,
  Server,
  Terminal,
  MessageSquare,
  Send,
  Loader
} from 'lucide-react';

// --- 粒子背景组件 (Neural Network Effect) ---
const ParticleBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width, height;
    let particles = [];

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    const createParticles = () => {
      particles = [];
      const count = Math.floor((width * height) / 15000);
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          size: Math.random() * 2 + 1,
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      ctx.strokeStyle = 'rgba(6, 182, 212, 0.15)';
      ctx.lineWidth = 1;

      for (let i = 0; i < particles.length; i++) {
        let p = particles[i];

        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        ctx.fillStyle = 'rgba(6, 182, 212, 0.8)';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        for (let j = i + 1; j < particles.length; j++) {
          let p2 = particles[j];
          let distance = Math.sqrt((p.x - p2.x) ** 2 + (p.y - p2.y) ** 2);
          if (distance < 120) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }
      requestAnimationFrame(draw);
    };

    resize();
    createParticles();
    draw();

    window.addEventListener('resize', () => {
      resize();
      createParticles();
    });

    return () => window.removeEventListener('resize', resize);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none z-0 bg-slate-950"
    />
  );
};

// --- AI 核心终端组件 (Gemini Powered) ---
const AICoreTerminal = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    { role: 'system', text: '连接建立成功...\n身份验证通过...\n正在接入 [MU_YAN_CORE_V9]...\n\n你好，人类。我是暮宴量化的核心算法中枢。关于我们的 Alpha 策略、风控模型或市场观点，你可以向我提问。' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [messages, isOpen]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMsg = input;
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch("/api/ai/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userMsg
        }),
      });

      if (!response.ok) throw new Error("Neural Link Failed");

      const data = await response.json();
      const aiText = data.choices?.[0]?.message?.content?.trim() || "系统异常：无法解码核心信号。";

      setMessages(prev => [...prev, { role: 'ai', text: aiText }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'system', text: '错误：与核心的连接被防火墙阻断。请稍后重试。' }]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-2xl bg-slate-950 border border-cyan-500/50 rounded-lg shadow-[0_0_50px_rgba(6,182,212,0.2)] overflow-hidden flex flex-col h-[600px]">
        {/* Header */}
        <div className="bg-slate-900/80 p-3 flex justify-between items-center border-b border-cyan-500/30">
          <div className="flex items-center gap-2 text-cyan-400">
            <Terminal size={18} />
            <span className="text-sm font-mono font-bold tracking-widest">MU_YAN_CORE_TERMINAL // ONLINE</span>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 font-mono bg-black/40">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] p-3 rounded ${msg.role === 'user'
                ? 'bg-cyan-900/20 text-cyan-100 border border-cyan-500/20'
                : msg.role === 'system'
                  ? 'text-yellow-500 text-xs leading-relaxed'
                  : 'text-green-400 leading-relaxed border-l-2 border-green-500 pl-3'
                }`}>
                {msg.role === 'ai' && <span className="block text-[10px] text-green-700 mb-1 uppercase tracking-wider">System Response</span>}
                <div className="whitespace-pre-wrap text-sm md:text-base">
                  {msg.text}
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-green-500 text-sm animate-pulse">
              <Loader className="animate-spin" size={14} />
              <span>COMPUTING PROBABILITY VECTORS...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 bg-slate-900/80 border-t border-cyan-500/30">
          <div className="flex gap-2">
            <span className="text-cyan-500 font-mono pt-2">{'>'}</span>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="输入指令或询问市场策略..."
              className="flex-1 bg-transparent text-white focus:outline-none resize-none h-12 font-mono text-sm pt-2"
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="text-cyan-500 hover:text-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed px-2 transition-colors"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- 导航栏组件 ---
const Navbar = ({ onOpenTerminal }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: '核心技术', href: '#tech' },
    { name: '策略表现', href: '#performance' },
    { name: '关于暮宴', href: '#about' },
    { name: '合作洽谈', href: '#contact' },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-slate-950/85 backdrop-blur-md border-b border-cyan-900/40 shadow-lg shadow-cyan-500/10' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex-shrink-0 flex items-center gap-3 cursor-pointer">
            <img
              src="/logo512.png"
              alt="暮宴量化 Logo"
              className="h-10 w-10 rounded-2xl bg-slate-950/80 object-cover shadow-[0_0_22px_rgba(56,189,248,0.55)] ring-1 ring-cyan-400/60 ring-offset-2 ring-offset-slate-950"
            />
            <span className="text-2xl md:text-3xl font-black tracking-[0.1em] bg-clip-text text-transparent bg-gradient-to-r from-white via-cyan-100 to-cyan-300 drop-shadow">
              暮宴量化
            </span>
          </div>

          <div className="hidden md:flex items-center gap-6">
            <div className="ml-10 flex items-baseline space-x-8">
              {navLinks.map((link) => (
                <a key={link.name} href={link.href} className="text-gray-300 hover:text-cyan-400 hover:scale-105 transition-all duration-300 px-3 py-2 rounded-md text-sm font-medium tracking-wide">
                  {link.name}
                </a>
              ))}
            </div>
            <button
              onClick={onOpenTerminal}
              className="bg-purple-600/20 border border-purple-500/50 text-purple-300 hover:bg-purple-600 hover:text-white px-4 py-2 rounded transition-all duration-300 text-sm font-bold flex items-center gap-2 animate-pulse hover:animate-none"
            >
              <Zap size={14} />
              <span>✨ 连接核心</span>
            </button>
            <button className="bg-cyan-500/10 border border-cyan-500/50 text-cyan-400 hover:bg-cyan-500 hover:text-black px-4 py-2 rounded transition-all duration-300 text-sm font-bold">
              合作伙伴通道
            </button>
          </div>

          <div className="-mr-2 flex md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="text-gray-400 hover:text-white p-2">
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-slate-900 border-b border-slate-800">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} className="text-gray-300 hover:text-cyan-400 block px-3 py-2 rounded-md text-base font-medium" onClick={() => setIsOpen(false)}>
                {link.name}
              </a>
            ))}
            <button onClick={() => { setIsOpen(false); onOpenTerminal(); }} className="w-full text-left text-purple-400 px-3 py-2 font-bold flex items-center gap-2">
              <Zap size={14} /> 连接核心
            </button>
            <button className="w-full text-left text-cyan-400 px-3 py-2 font-bold">
              合作伙伴通道
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

// --- 故障文字效果组件 ---
const GlitchText = ({ text }) => {
  return (
    <div className="relative inline-block group">
      <h1 className="text-6xl md:text-8xl font-black text-white tracking-tighter mb-6 relative z-10">
        {text}
      </h1>
      <h1 className="text-6xl md:text-8xl font-black text-cyan-500 tracking-tighter mb-6 absolute top-0 left-0 -z-10 opacity-0 group-hover:opacity-70 animate-pulse translate-x-[2px]">
        {text}
      </h1>
      <h1 className="text-6xl md:text-8xl font-black text-purple-500 tracking-tighter mb-6 absolute top-0 left-0 -z-10 opacity-0 group-hover:opacity-70 animate-pulse -translate-x-[2px]">
        {text}
      </h1>
    </div>
  );
};

// --- 功能卡片组件 ---
const FeatureCard = ({ icon: Icon, title, desc }) => (
  <div className="group relative p-7 bg-slate-900/60 border border-slate-800/70 hover:border-cyan-500/50 transition-all duration-500 rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
    <div className="absolute inset-px rounded-xl bg-gradient-to-br from-cyan-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
    <div className="relative z-10">
      <div className="w-12 h-12 bg-slate-800/80 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 border border-slate-700/70 group-hover:border-cyan-500/40 shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
        <Icon className="text-cyan-300" size={24} />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-cyan-200 transition-colors">{title}</h3>
      <p className="text-slate-200/90 text-sm leading-relaxed">{desc}</p>
    </div>
  </div>
);

// --- 滚动数字组件 ---
const StatNumber = ({ value, label, prefix = "", suffix = "" }) => (
  <div className="text-center p-4">
    <div className="text-4xl md:text-5xl font-mono font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 mb-2">
      {prefix}{value}{suffix}
    </div>
    <div className="text-xs md:text-sm text-gray-500 uppercase tracking-widest font-semibold">{label}</div>
  </div>
);

// --- 主应用组件 ---
export default function App() {
  const [isTerminalOpen, setIsTerminalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans selection:bg-cyan-500 selection:text-black overflow-x-hidden relative">
      <ParticleBackground />
      <Navbar onOpenTerminal={() => setIsTerminalOpen(true)} />

      {/* AI Core Terminal Modal */}
      <AICoreTerminal isOpen={isTerminalOpen} onClose={() => setIsTerminalOpen(false)} />

      {/* Floating Action Button for AI Core */}
      {!isTerminalOpen && (
        <button
          onClick={() => setIsTerminalOpen(true)}
          className="fixed bottom-8 right-8 z-40 bg-slate-900 border border-purple-500 text-purple-400 hover:bg-purple-600 hover:text-white p-4 rounded-full shadow-[0_0_20px_rgba(168,85,247,0.4)] hover:shadow-[0_0_40px_rgba(168,85,247,0.6)] transition-all duration-300 group"
        >
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping"></div>
          <MessageSquare size={24} className="group-hover:scale-110 transition-transform" />
        </button>
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-24 pb-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center">
          <div className="inline-block px-4 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-400 text-xs font-semibold tracking-[0.2em] mb-10 backdrop-blur-sm shadow-[0_0_30px_rgba(6,182,212,0.25)]">
            AI-POWERED QUANTITATIVE TRADING
          </div>

          <GlitchText text="暮 宴 量 化" />

          <p className="text-xl md:text-2xl text-slate-200 max-w-3xl mx-auto mb-12 font-light leading-9">
            在暮色与未来的交界处，用 <span className="text-cyan-300 font-semibold">深度学习</span> 重构金融逻辑。
            <br className="hidden md:block" />
            捕捉每一次脉冲，为了最后的盛宴。
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => setIsTerminalOpen(true)}
              className="px-9 py-4 bg-cyan-500/90 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl transition-all duration-300 shadow-[0_10px_40px_rgba(6,182,212,0.35)] hover:shadow-[0_15px_50px_rgba(6,182,212,0.45)] flex items-center gap-3 border border-cyan-400/40"
            >
              <Zap size={18} /> 呼叫 AI 核心 <ChevronRight className="group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href="#contact"
              className="px-9 py-4 bg-white/5 border border-white/10 hover:border-cyan-200/60 text-white font-semibold rounded-xl transition-all duration-300 backdrop-blur-md hover:bg-white/10 shadow-[0_0_30px_rgba(255,255,255,0.05)] flex items-center justify-center"
            >
              联系我们
            </a>
          </div>
        </div>

        {/* 底部装饰条 */}
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-900 to-transparent"></div>
      </section>

      {/* Stats Section */}
      <section className="relative z-10 border-y border-slate-900/50 bg-slate-950/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          <StatNumber value="50" suffix="us" label="平均执行延迟" />
          <StatNumber value="24" suffix="/7" label="AI 监控时长" />
          <StatNumber value="100" suffix="+" label="专有因子库" />
          <StatNumber value="99.9" suffix="%" label="系统可用性" />
        </div>
      </section>

      {/* Technology Section */}
      <section id="tech" className="relative py-24 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-500">
                核心引擎
              </span>
            </h2>
            <div className="w-20 h-1 bg-cyan-500 mx-auto rounded-full"></div>
            <p className="mt-4 text-gray-400">超越传统统计套利，拥抱神经网络的非线性预测能力</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard
              icon={Cpu}
              title="深度神经网络"
              desc="利用 Transformer 和 LSTM 架构处理多模态金融数据，挖掘肉眼不可见的市场规律。"
            />
            <FeatureCard
              icon={Zap}
              title="超低延迟执行"
              desc="FPGA 硬件加速配合自研 C++ 极速交易柜台，确保在毫秒级竞争中始终领先一步。"
            />
            <FeatureCard
              icon={Shield}
              title="自适应风控"
              desc="动态风险模型实时计算 VaR，极端行情下 AI 自动熔断，守护资产安全底线。"
            />
            <FeatureCard
              icon={Activity}
              title="多因子Alpha"
              desc="整合基本面、量价、舆情等多维数据，构建数千个低相关性的 Alpha 因子组合。"
            />
            <FeatureCard
              icon={Globe}
              title="全球市场覆盖"
              desc="统一技术底座覆盖股票、期货、期权及数字资产等多市场多品种，为策略提供跨市场的信号与执行通道。"
            />
            <FeatureCard
              icon={Server}
              title="分布式算力"
              desc="基于 Kubernetes 的大规模并行回测系统，分钟级完成十年历史数据策略验证。"
            />
          </div>
        </div>
      </section>

      {/* About Section (Split Layout) */}
      <section id="about" className="relative py-24 z-10 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-20 h-20 border-t-2 border-l-2 border-cyan-500/30"></div>
              <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b-2 border-r-2 border-purple-500/30"></div>

              <div className="bg-gradient-to-br from-slate-800 to-slate-950 p-8 rounded-lg border border-slate-700 shadow-2xl">
                <div className="font-mono text-xs text-cyan-500 mb-4">
                  &gt; SYSTEM_STATUS: ONLINE<br />
                  &gt; LOADING_MODULE: TEAM_INTRO...
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">数学与代码的炼金术师</h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  暮宴量化 (Mu Yan Quant) 成立于 2025 年，汇聚了来自华尔街顶级投行的资深交易员与硅谷科技公司的 AI 科学家和工程师。
                </p>
                <p className="text-gray-400 leading-relaxed">
                  我们不相信水晶球，我们相信概率论。在喧嚣的市场噪音中，我们致力于寻找那些恒久的数学真理。
                  暮宴量化是一家以数据为燃料的 AI 量化科技公司，专注于策略研发、交易系统与风控基础设施的构建，并为合作伙伴提供可落地的量化解决方案。
                </p>
                <div className="mt-6 flex gap-4">
                  <div className="flex flex-col">
                    <span className="text-3xl font-bold text-white">10+</span>
                    <span className="text-xs text-gray-500 uppercase">核心策略组合</span>
                  </div>
                  <div className="w-px bg-slate-700"></div>
                  <div className="flex flex-col">
                    <span className="text-3xl font-bold text-white">30+</span>
                    <span className="text-xs text-gray-500 uppercase">核心成员</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <h4 className="flex items-center gap-2 text-xl font-bold text-white mb-2">
                  <Code className="text-purple-500" /> 我们的哲学
                </h4>
                <p className="text-gray-400">
                  市场是无效的，但人性是永恒的。通过AI捕捉人性的弱点，通过严苛的风控规避自身的弱点。
                </p>
              </div>
              <div>
                <h4 className="flex items-center gap-2 text-xl font-bold text-white mb-2">
                  <TrendingUp className="text-cyan-500" /> 我们的愿景
                </h4>
                <p className="text-gray-400">
                  打造下一代智能量化科技平台，让每一次策略决策都拥有大规模计算与 AI 的加持。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative py-24 z-10">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8">合作洽谈</h2>
          <p className="text-gray-400 mb-10">
            我们专注于 AI 量化基础设施与模型工程，欢迎就合作或人才加入与我们联系。<br />
            本页面仅用于技术交流，不构成任何投资建议或资产管理承诺。
          </p>

          <div className="grid md:grid-cols-2 gap-6 text-left mb-10">
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-2">机构合作</h3>
              <p className="text-gray-300 text-sm mb-2">
                策略合作 / 系统对接 / 数据合作
              </p>
              <p className="text-gray-500 text-xs">
                适用于券商、自营、量化私募、对冲基金及其他专业机构，在模型、数据或执行层面需要建立深度协作。
              </p>
            </div>
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-2">人才招募</h3>
              <p className="text-gray-300 text-sm mb-2">
                机器学习 / 量化研究 / 系统工程
              </p>
              <p className="text-gray-500 text-xs mb-4">
                面向对金融、算法与工程同样痴迷的伙伴，长期开放技术向岗位。
              </p>
              <button className="inline-flex items-center px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold transition-colors">
                加入我们（Careers）
              </button>
            </div>
          </div>

          <form className="space-y-4 text-left bg-slate-900/50 p-8 rounded-2xl border border-slate-800 backdrop-blur-sm">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">姓名 / Name</label>
                <input
                  type="text"
                  className="w-full bg-slate-800 border border-slate-700 rounded p-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                  placeholder="您的称呼"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">邮箱 / Email</label>
                <input
                  type="email"
                  className="w-full bg-slate-800 border border-slate-700 rounded p-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                  placeholder="you@muyanquant.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">合作意向 / Message</label>
              <textarea
                className="w-full bg-slate-800 border border-slate-700 rounded p-3 text-white focus:outline-none focus:border-cyan-500 transition-colors h-32"
                placeholder="请简单描述您的机构背景或技术诉求..."
              ></textarea>
            </div>
            <button className="w-full py-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold rounded shadow-lg transform hover:-translate-y-1 transition-all">
              发送信息
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 bg-black border-t border-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
            <img
              src="/logo512.png"
              alt="暮宴量化 Logo"
              className="h-8 w-8 rounded-lg mr-2 bg-slate-950/80 shadow-[0_0_18px_rgba(56,189,248,0.5)] ring-1 ring-cyan-400/60 ring-offset-[3px] ring-offset-black"
            />
              <div>
                <span className="font-black text-xl tracking-[0.18em] bg-clip-text text-transparent bg-gradient-to-r from-white via-cyan-100 to-cyan-300">
                  暮宴量化
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  构建 AI 驱动的量化模型与交易系统基础设施，致力于将工程能力转化为可落地的量化技术体系。
                </p>
              </div>
            </div>
            <div className="text-gray-500 text-sm text-center md:text-right">
              <p>© 2025 Mu Yan Quantitative Technology. 技术服务，不提供资管或投顾。</p>
              <p className="mt-1 text-xs">
                本网站内容仅用于技术展示及学术交流，不构成任何证券、期货或其他金融产品的推荐或要约。
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-gray-500 text-sm">
            <div className="space-y-1">
              <div>公司介绍 / About：暮宴量化科技</div>
              <div>联系方式 / Contact：+86-000-0000-000</div>
              <div>
                备案信息 / ICP：<span>蜀ICP备2025172749号</span>
              </div>
            </div>
            <div className="space-y-1">
              <div>合作邮箱：cooperation@muyanquant.com</div>
              <div>招聘邮箱：careers@muyanquant.com</div>
              <div>媒体邮箱：media@muyanquant.com</div>
            </div>
            <div className="flex flex-wrap gap-4">
              <a href="/privacy" className="hover:text-cyan-400 transition-colors">
                隐私政策
              </a>
              <a href="/terms" className="hover:text-cyan-400 transition-colors">
                使用条款
              </a>
              <a href="/compliance" className="hover:text-cyan-400 transition-colors">
                法律声明
              </a>
              <a href="/whitepaper" className="hover:text-cyan-400 transition-colors">
                技术白皮书
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
